import { useMemo, useRef, useState } from 'react'
import { ImageOverlay, MapContainer, Marker } from 'react-leaflet'
import { useParams } from 'react-router-dom'
import Const from 'constants/constant'
import { CRS, LatLngTuple } from 'leaflet'
import {
  numberIcon,
  rotatedMarkerIcon
} from 'pages/building/floor/area/CustomMarker'
import { useRecoilState } from 'recoil'
import { currentPovState, povPerPointState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { FloorDetail, FloorMapElement, Point } from 'types/building'
import { LeafletRef } from 'types/leafletMap'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import CloseIcon from 'components/icons/CloseIcon'

import useViewBuildingDetail from '../hooks/useViewBuildingDetail'

export default function ViewerMiniMap() {
  const { id } = useParams()

  const mapContainerPreviewRef = useRef<any>(null)
  const leafletRef = useRef<LeafletRef>({
    mapContainerRef: null,
    povMarkerRef: null,
    radarDragFlag: {
      isRadarMode: false,
      isDragging: false,
      rotate: null,
      hgap: null,
      mouseHoldTime: null
    }
  })
  const [previewImgBounds, setPreviewImgBounds] = useState<LatLngTuple>([0, 0])

  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)

  const { data: floorDetail, mutate: refetchFloor } = useSWR<FloorDetail>(
    currentViewer?.selectedFloor
      ? {
          url: `v1/floors/${currentViewer?.selectedFloor}`
        }
      : null,
    {
      revalidateOnMount: false
    }
  )

  const { data: mapItems } = useSWR<FloorMapElement>(
    currentViewer?.selectedFloor
      ? {
          url: 'v1/points',
          params: {
            buildingId: id,
            floorId: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      revalidateOnMount: false
    }
  )

  const imagePath = useMemo(
    () => floorDetail?.mapFile.s3Path || '',
    [floorDetail?.mapFile.s3Path]
  )
  const points = useMemo(() => mapItems?.points || [], [mapItems?.points])

  const { mapOnReady } = useViewBuildingDetail({
    imagePath,
    refetchFloor
  })

  const handleCloseMiniMap = () => {
    setCurrentViewer((prev) => ({ ...prev, isOpenMiniMap: false }))
  }

  const markerEventHandler = (point: Point) => {
    return {
      click() {
        // Reset currentPov so it won't reuse the previous navigation's POV
        setCurrentPov(null)
        // Also clear stored POV for the target point so viewer falls to initView
        setPovPerPoint((prev) => {
          const next = { ...prev }
          delete next[point.id]
          return next
        })
        setCurrentViewer((prev) => ({
          ...prev,
          selectedImage: point.image360?.id,
          selectedArea: `${point.areaId}`,
          selectedPoint: point.id,
          // Treat as left-menu selection so embedpano will reset POV to default
          isSelectPointInLeftMenu: true,
          isClickToDirection: false,
          isClickToNextFloor: false
        }))

        // Update URL
        const urlToUpdate = new URL(window.location.href)
        urlToUpdate.searchParams.set('point', point.id)
        urlToUpdate.searchParams.set('area', String(point.areaId))
        urlToUpdate.searchParams.delete('subimage')
        urlToUpdate.searchParams.delete('hlookat')
        urlToUpdate.searchParams.delete('vlookat')
        urlToUpdate.searchParams.delete('fov')
        urlToUpdate.searchParams.delete('radar_angle')
        window.history.pushState({}, '', urlToUpdate.toString())

        handleCloseMiniMap()
      }
    }
  }

  const displayRadar = useMemo(() => {
    const radarPoint = points.find(
      (point) => point.id === currentViewer.selectedPoint
    )

    if (radarPoint && radarPoint.image360) {
      const viewer = new KrpanoEngine('viewer')

      const rotate =
        viewer.hlookat !== null
          ? Krpano.NormalizeHGap(viewer.hlookat)
          : radarPoint.initView.h

      const pointObj =
        (currentViewer.pointDetail?.id === radarPoint.id
          ? currentViewer.pointDetail
          : radarPoint) || radarPoint

      const activeImageId =
        viewer.renderedImageId ||
        currentViewer.selectedImage ||
        pointObj.image360?.id

      const radarAngle = Krpano.GetRadarAngle(rotate, pointObj, activeImageId)

      return (
        <Marker
          position={[radarPoint.y, radarPoint.x]}
          ref={(el) => {
            leafletRef.current.povMarkerRef = el
          }}
          zIndexOffset={430 + points.length}
          icon={rotatedMarkerIcon(
            leafletRef.current.radarDragFlag.rotate
              ? leafletRef.current.radarDragFlag.rotate
              : radarAngle
          )}
          draggable={false}
        />
      )
    }
    return null
  }, [
    currentViewer.selectedPoint,
    currentViewer.pointDetail,
    currentViewer.selectedImage,
    points
  ])

  return imagePath ? (
    <div className="absolute top-0 left-0 w-full h-full flex bg-[#00000099] z-[700]">
      <div className="m-auto flex w-[1006px] h-[580px] bg-white relative rounded-[20px]">
        <div
          className="absolute top-5 right-5 cursor-pointer"
          onClick={handleCloseMiniMap}
        >
          <CloseIcon />
        </div>
        <div className="w-[700px] h-[490px] m-auto">
          <MapContainer
            ref={(el) => {
              leafletRef.current.mapContainerRef = el
              mapContainerPreviewRef.current = el
            }}
            center={[-99999, -99999]}
            zoom={0}
            zoomSnap={0.1}
            zoomDelta={0.5}
            className="area-map-container"
            crs={CRS.Simple}
            zoomControl={false}
            attributionControl={false}
            preferCanvas
            whenReady={() => {
              mapOnReady(mapContainerPreviewRef, setPreviewImgBounds)
            }}
          >
            {points.map((point) => (
              <Marker
                key={`preview-marker-${point.id}`}
                draggable={false}
                bubblingMouseEvents={false}
                eventHandlers={markerEventHandler(point)}
                zIndexOffset={
                  point.id === currentViewer.selectedPoint
                    ? 450
                    : 400 + point.order
                }
                position={[point.y, point.x]}
                icon={numberIcon(
                  point,
                  false,
                  point.id === currentViewer.selectedPoint,
                  true
                )}
                opacity={Const.OPACITY_OF_POINT_MAP}
              />
            ))}

            {displayRadar}

            <ImageOverlay url={imagePath} bounds={[[0, 0], previewImgBounds]} />
          </MapContainer>
        </div>
      </div>
    </div>
  ) : null
}
